import type { ColumnFiltersState } from "@tanstack/react-table";
import type { FilterDescriptor, FilterOperator, FilterValue } from "./filterTypes";
import {
  validateFilterArray,
  validateFilterValue,
} from "@/lib/validation/filterSchemas";
import {
  sanitizeFilterValue,
  sanitizeFieldName,
  sanitizeString,
} from "@/lib/validation/sanitize";
import { logger } from "@/lib/utils/logger";

export interface FrappeFilter {
  [key: string]: any; // Can be value, [operator, value], or ["in", [values]]
}

/**
 * Frappe filter array format: [["doctype", "field", "operator", "value"], ...]
 * Or for or_filters: [["field", "operator", "value"], ...]
 */
export type FrappeFilterArray = Array<
  | [string, string, string, any] // [doctype, field, operator, value]
  | [string, string, any]         // [field, operator, value] (for or_filters)
>;

/**
 * Map internal FilterOperator to ERPNext operator string
 * 
 * Note: isSet and isNotSet are handled specially in the switch statement
 * (they use "is" operator with "set"/"not set" values), so their mapping
 * here is not used but kept for completeness.
 */
const OPERATOR_TO_FRAPPE: Record<FilterOperator, string> = {
  equals: "=",
  notEquals: "!=",
  like: "like",
  notLike: "not like",
  in: "in",
  notIn: "not in",
  greaterThan: ">",
  lessThan: "<",
  greaterOrEqual: ">=",
  lessOrEqual: "<=",
  between: "between",
  isSet: "is", // Actually uses "is" with "set" value (handled in switch)
  isNotSet: "is", // Actually uses "is" with "not set" value (handled in switch)
  is: "is",
  timespan: "timespan",
};

/**
 * Convert FilterDescriptor[] to ERPNext filter array format
 * 
 * ERPNext accepts filters as:
 * - Array: [["doctype", "field", "operator", "value"], ...]
 * - Object: { field: value } or { field: [operator, value] }
 * 
 * This function returns array format for maximum compatibility.
 * 
 * @param doctype - The DocType name (e.g., "Item")
 * @param filters - Array of filter descriptors
 * @returns Frappe filter array format
 */
export function mapFiltersToFrappe(
  doctype: string,
  filters: FilterDescriptor[]
): any[] {
  // Validate filters array. We used to hard-throw here, which crashed the
  // entire route because this runs synchronously during render (no
  // ErrorBoundary, no queryFn async context). Now we log and return an empty
  // array so the caller degrades to "no filters" instead of blowing up the
  // page. The real guard is caller-side (skip the query when inputs aren't
  // ready) — this is hardening for the remaining edge cases. See OPS-1531.
  const validation = validateFilterArray(filters);
  if (!validation.success) {
    logger.warn("[mapFiltersToFrappe] Invalid filter array, returning empty filters:", {
      issues: validation.error?.issues,
      filters,
    });
    return [];
  }

  const frappeFilters: any[] = [];

  for (const filter of filters) {
    // Sanitize field name
    const field = sanitizeFieldName(filter.field);
    const operator = filter.operator;
    
    // CRITICAL: Use child doctype if present (4-tuple), otherwise use parent doctype (3-tuple)
    // Do NOT always output 4-tuple - preserve canonical format
    const hasChildDoctype = !!filter.childDoctype;
    const targetDoctype = filter.childDoctype || doctype;
    
    // REMOVED: Legacy hardcoded mappings (no longer needed once childDoctype is properly supported)
    
    // Sanitize and validate value
    const value = sanitizeFilterValue(filter.value);
    
    // Validate value based on operator
    const valueValidation = validateFilterValue(operator, value as FilterValue);
    if (!valueValidation.valid) {
      logger.warn(`Invalid filter value for ${field}:`, valueValidation.error);
      continue; // Skip invalid filters
    }

    // Skip filters with no value (unless it's isSet/isNotSet/is)
    if (value === null || value === undefined || value === "") {
      if (operator !== "isSet" && operator !== "isNotSet" && operator !== "is") {
        continue;
      }
    }

    const frappeOp = OPERATOR_TO_FRAPPE[operator];

    switch (operator) {
      case "equals":
      case "notEquals":
      case "greaterThan":
      case "lessThan":
      case "greaterOrEqual":
      case "lessOrEqual":
        // Convert Check (boolean) fields to 1/0 (Frappe convention)
        let finalValue = value;
        if (typeof value === "boolean") {
          finalValue = value ? 1 : 0;
        }
        // CRITICAL: Output 3-tuple for parent, 4-tuple for child
        if (hasChildDoctype) {
          frappeFilters.push([targetDoctype, field, frappeOp, finalValue]);
        } else {
          frappeFilters.push([field, frappeOp, finalValue]);  // 3-tuple
        }
        break;

      case "like":
      case "notLike":
        const likeValue =
          typeof value === "string"
            ? value.includes("%")
              ? value
              : `%${value}%`
            : String(value);
        if (hasChildDoctype) {
          frappeFilters.push([targetDoctype, field, frappeOp, likeValue]);
        } else {
          frappeFilters.push([field, frappeOp, likeValue]);  // 3-tuple
        }
        break;

      case "in":
      case "notIn":
        const inValue = Array.isArray(value) ? value : [value];
        if (hasChildDoctype) {
          frappeFilters.push([targetDoctype, field, frappeOp, inValue]);
        } else {
          frappeFilters.push([field, frappeOp, inValue]);  // 3-tuple
        }
        break;

      case "between":
        if (Array.isArray(value) && value.length === 2) {
          if (hasChildDoctype) {
            frappeFilters.push([targetDoctype, field, frappeOp, value]);
          } else {
            frappeFilters.push([field, frappeOp, value]);  // 3-tuple
          }
        }
        break;

      case "isSet":
        if (hasChildDoctype) {
          frappeFilters.push([targetDoctype, field, "is", "set"]);
        } else {
          frappeFilters.push([field, "is", "set"]);  // 3-tuple
        }
        break;

      case "isNotSet":
        if (hasChildDoctype) {
          frappeFilters.push([targetDoctype, field, "is", "not set"]);
        } else {
          frappeFilters.push([field, "is", "not set"]);  // 3-tuple
        }
        break;

      case "is":
        const isValue = value || "None";
        if (hasChildDoctype) {
          frappeFilters.push([targetDoctype, field, frappeOp, isValue]);
        } else {
          frappeFilters.push([field, frappeOp, isValue]);  // 3-tuple
        }
        break;

      case "timespan":
        const timespanValue = typeof value === "string" 
          ? sanitizeString(value) 
          : String(value);
        if (hasChildDoctype) {
          frappeFilters.push([targetDoctype, field, frappeOp, timespanValue]);
        } else {
          frappeFilters.push([field, frappeOp, timespanValue]);  // 3-tuple
        }
        break;
    }
  }

  return frappeFilters;
}

/**
 * Validate filters before sending to backend
 * 
 * @param _doctype - The DocType name (unused, kept for API consistency)
 * @param filters - Array of filter descriptors to validate
 * @returns Validation result with errors if any
 */
export function validateFilters(
  _doctype: string,
  filters: FilterDescriptor[]
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  for (const filter of filters) {
    if (!filter.field) {
      errors.push("Filter missing field name");
    }
    if (!filter.operator) {
      errors.push(`Filter for ${filter.field || "unknown"} missing operator`);
    }
    if (
      filter.value === null ||
      filter.value === undefined ||
      filter.value === ""
    ) {
      if (
        filter.operator !== "isSet" &&
        filter.operator !== "isNotSet" &&
        filter.operator !== "is"
      ) {
        errors.push(`Filter for ${filter.field} missing value`);
      }
    }
    if (filter.operator === "between") {
      if (!Array.isArray(filter.value) || filter.value.length !== 2) {
        errors.push(
          `Filter for ${filter.field}: between requires array of 2 values`
        );
      }
    }
    if (filter.operator === "in" || filter.operator === "notIn") {
      if (!Array.isArray(filter.value)) {
        errors.push(
          `Filter for ${filter.field}: in/notIn requires array value`
        );
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Converts TanStack Table column filters to Frappe API filter format
 * 
 * @deprecated Use mapFiltersToFrappe() instead. This function is kept for backward compatibility.
 * 
 * @param columnFilters - Array of column filters from TanStack Table
 * @returns Frappe filter object compatible with Frappe API
 * 
 * @example
 * // Text search
 * { item_code: ["like", "%pepsi%"] }
 * 
 * // Multi-select
 * { item_group: ["in", ["Beverages", "Food"]] }
 * 
 * // Exact match
 * { is_stock_item: 1 }
 */
export function buildFrappeFilters(
  columnFilters: ColumnFiltersState
): FrappeFilter {
  const filters: FrappeFilter = {};

  for (const filter of columnFilters) {
    const { id, value } = filter;
    
    // Skip empty/null/undefined values
    if (value === undefined || value === null || value === "") {
      continue;
    }

    switch (id) {
      case "item_code":
      case "item_name":
        // Text search with LIKE operator and wildcards
        // Frappe format: ["like", "%value%"]
        if (typeof value === "string" && value.trim()) {
          filters[id] = ["like", `%${value.trim()}%`];
        }
        break;

      case "item_group":
      case "stock_uom":
        // Multi-select with IN operator
        // Frappe format: ["in", [value1, value2, ...]]
        if (Array.isArray(value)) {
          if (value.length === 0) {
            continue; // Skip empty arrays
          } else if (value.length === 1) {
            // Optimize single value to exact match
            filters[id] = ["=", value[0]];
          } else {
            filters[id] = ["in", value];
          }
        } else if (value) {
          // Single value - use exact match
          filters[id] = ["=", value];
        }
        break;

      case "standard_rate":
        // Number filter - exact match
        // Can be extended for range queries later
        const numValue = Number(value);
        if (!isNaN(numValue)) {
          filters[id] = ["=", numValue];
        }
        break;

      case "is_stock_item":
        // Boolean filter
        // Frappe uses 1/0 for boolean values
        filters[id] = value ? 1 : 0;
        break;

      default:
        // Fallback: exact match for unknown filter types
        filters[id] = value;
    }
  }

  return filters;
}

